import { PLANS } from "constant/plan";

export const hasValidConversationComponentRoute = (route: string): boolean => {
  const invalidRoutes = [
    '/profile', 
    '/organization-management', 
    '/document-management', 
    '/comingsoon', 
  ];
  return !invalidRoutes.includes(route);
};


export const hasValidGroupChatComponentRoute = (route: string): boolean => {
  const invalidRoutes = [
    '/project', 
    '/custom-gpts', 
    '/identity-management', 
    '/profile', 
    '/organization-management', 
    '/document-management', 
    '/media-library', 
    '/comingsoon',
    "/agentChat",
    "/agents", 
    '/models',
    '/tools',
    '/knowledge-base',
    '/skills',
    '/prompts',
    '/integrations',
    '/workflows',
    '/usage',
    '/plans-billing',
  ];
  return !invalidRoutes.some((invalidRoute) => route.startsWith(invalidRoute));
};
  
export const hasValidPlanForGroupChat = (plan: any | null): boolean => {
  const effectivePlanName = plan?.name ?? PLANS.UNLIMITED;
  const invalidPlans = [PLANS.NOPLAN.valueOf()];
  return !invalidPlans.includes(effectivePlanName);
};

export const hasValidPlanForStartViaShareLink = (plan: any | null): boolean => {
  const effectivePlanName = plan?.name ?? PLANS.UNLIMITED;
  const validPlans = [PLANS.FREE.valueOf(), PLANS.BASIC.valueOf(), PLANS.UNLIMITED.valueOf()];
  return validPlans.includes(effectivePlanName);
};

// Helper function to capitalize first letter
export function capitalizeFirstLetter(string: string) {
    // Handle comma-separated values (e.g., "Android, ios" -> "Android/iOS")
    if (string.includes(',')) {
        return string
            .split(',')
            .map(s => s.trim())
            .map(s => {
                if (s.toLowerCase() === 'ios') {
                    return 'iOS';
                }
                return s[0].toUpperCase() + s.slice(1).toLowerCase();
            })
            .join('/');
    }

    // Special case for iOS
    if (string.toLowerCase() === 'ios') {
        return 'iOS';
    }
    return string[0].toUpperCase() + string.slice(1).toLowerCase();
}

// Helper function to format compliance values
export function formatCompliance(string: string) {
    // Handle comma-separated values
    return string
        .split(',')
        .map(s => s.trim())
        .map(s => {
            // Replace underscores with spaces and handle special cases
            switch (s.toUpperCase()) {
                case 'DSG':
                    return 'DSG';
                case 'ISO_27001':
                    return 'ISO 27001';
                case 'GDPR':
                    return 'GDPR';
                case 'EU_AI_ACT':
                    return 'EU AI Act';
                default:
                    // For any other value, replace underscores with spaces
                    return s.replace(/_/g, ' ');
            }
        })
        .join(', ');
}

// Interface for feature objects
interface Feature {
    translationKey: string;
    value: string;
}

// Process feature translation key and display value
export function processFeatureDisplay(
    feature: Feature,
    allFeatures: Feature[]
): { translationKey: string; displayValue: string } {
    // If feature is IMAGE_GENERATION_DEFAULT and value is DEFAULT, use IMAGE_DEFAULT translation key
    let translationKey = feature.translationKey;
    if (feature.translationKey === 'IMAGE_GENERATION_DEFAULT' && feature.value === 'DEFAULT') {
        translationKey = 'IMAGE_DEFAULT';
    }

    // If feature is IMAGE_GENERATION_DEFAULT, show DOCUMENT_UPLOAD value
    let displayValue = feature.translationKey === 'IMAGE_GENERATION_DEFAULT' && feature.value !== 'DEFAULT'
        ? allFeatures.find(f => f.translationKey === 'DOCUMENT_UPLOAD')?.value || feature.value
        : feature.value;

    // Capitalize value for MOBILE_APP
    if (feature.translationKey === 'MOBILE_APP' && typeof displayValue === 'string') {
        displayValue = capitalizeFirstLetter(displayValue);
    }

    // Format compliance values
    if (feature.translationKey === 'COMPLIANCE' && typeof displayValue === 'string') {
        displayValue = formatCompliance(displayValue);
    }

    return { translationKey, displayValue };
}
